/**
 * The seam between billing and the rest of Parsley.
 *
 * Usage enforcement, the paywall, and the meal-plan gate all ask this package one question — *is
 * this account entitled, and to what* — and know nothing about Stripe.
 *
 * ## Why this is a shared package and not two copies
 *
 * Entitlement is a **function of `now`**, not a stored field. The 14-day `past_due` grace expires
 * with **no Stripe event behind it**: the webhook writes `status: 'past_due'` once and nothing ever
 * fires again. A persisted `active: true` is correct the moment it's written and wrong forever
 * after. The same is true of `trialing` (the trial end passes before Stripe's pause lands) and of
 * `cancelAtPeriodEnd` (the renewal date passes).
 *
 * So both the client and the server have to evaluate it, continuously, from the same rules. The
 * repo mirrors types by hand elsewhere and that's fine for shapes — but here the boundary condition
 * is a time comparison, and the two sides disagreeing renders a working-looking "New plan" button
 * that throws `permission-denied` on click. Share the predicate; mirror only the shape.
 *
 * ## Plain data only
 *
 * Nothing here imports `firebase-admin`, `firebase`, or `stripe` — deliberately. The billing
 * document's timestamps are `firebase-admin/firestore`'s `Timestamp` on the server and
 * `firebase/firestore`'s on the client: different types from different packages. A shared interface
 * containing them would have to depend on one, which is what makes this package zero-dep at all.
 *
 * Each side keeps its own document interface and hands over {@link EntitlementInput} — the same
 * idiom `readLlmConfig` uses ("speaks plain data, never a Firestore type") and
 * `ProfileContext.readInviteQuota` uses (reduces `Timestamp` to epoch millis so consumers stay
 * Timestamp-free).
 */

/** Public names are Pinch and Handful; these are the keys Stripe knows, via `product.metadata.tier`. */
export enum BillingTier {
  Light = 'light',
  Heavy = 'heavy'
}

/**
 * Stripe's subscription statuses, verbatim. Deliberately **not** an internal enum — a translation
 * layer between Stripe's vocabulary and our own would drift the first time Stripe adds a status,
 * and every debugging session would start by decoding it.
 */
export enum SubscriptionStatus {
  Trialing = 'trialing',
  Active = 'active',
  PastDue = 'past_due',
  Paused = 'paused',
  Canceled = 'canceled',
  Unpaid = 'unpaid',
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired'
}

/**
 * Parsley's own field, mirrored to `subscription.metadata.cohort`. Drives email cadence and
 * analytics only — it does **not** affect entitlement, and Stripe never learns what "beta" means.
 */
export enum Cohort {
  Beta = 'beta',
  Trial = 'trial',
  Direct = 'direct'
}

/**
 * How long access survives a failed payment, measured from the transition into `past_due`.
 *
 * ## This number is coupled to a Stripe dashboard setting
 *
 * Settings → Billing → Subscriptions and emails → **Manage failed payments**, confirmed
 * 2026-08-04:
 *
 * - Smart Retries: **up to 8 attempts within 2 weeks**
 * - When all retries fail: **cancel the subscription** (the invoice is left past-due)
 *
 * So Stripe gives up at the same point this window closes, and the two agree. **Change one and
 * you must change the other**, because nothing connects them and the failure is silent:
 *
 * - Shorten Stripe's window → it cancels while this code still reports access, and the
 *   "N days left" countdown becomes a promise we don't keep
 * - Lengthen this constant → we serve someone whose subscription Stripe has already canceled
 *
 * Known imprecision, accepted: "8 retries within 2 weeks" means the last attempt can land on day
 * 13, and cancellation follows it — so Stripe may cancel while the UI still shows a day left. The
 * user isn't stranded (the mirror flips to `canceled`, which has its own route back through the
 * Customer Portal), they just lose the tail of the countdown. A consequence worth knowing: because
 * the two expire together, `grace_expired` may exist for only minutes in practice.
 *
 * ## Why it lives here
 *
 * A client-side pure function can't read a server-only Firestore document, so this can't go in
 * `config/billing` — and it's a policy constant rather than a knob. Changing it is a deploy, which
 * is the right amount of friction for "how long do we serve people who haven't paid".
 */
export const PAST_DUE_GRACE_DAYS = 14

const DAY_MS = 24 * 60 * 60 * 1000


/**
 * Milliseconds left in the past-due grace window. Never more than the window, never less than 0.
 *
 * Both bounds are load-bearing, and each guards a different bad anchor:
 *
 * **The ceiling.** `pastDueSince` comes from Stripe's clock (the failed invoice's timestamp) while
 * `now` comes from ours. Under a Stripe test clock those are different eras, so the anchor lands in
 * the future and the subtraction inflates — observed as a page reading "223 of 14 grace days left",
 * with entitlement staying active for the seven months the clock was ahead.
 *
 * **The floor.** An anchor far enough in the past would otherwise go negative.
 *
 * Shared rather than duplicated: the same number decides what the page says AND whether the account
 * is entitled, and those must never disagree.
 */
export function graceRemainingMs(pastDueSinceMs: number, nowMs: number): number {
  const window = PAST_DUE_GRACE_DAYS * DAY_MS
  return Math.min(window, Math.max(0, window - (nowMs - pastDueSinceMs)))
}


/**
 * A plain-data projection of `/users/{uid}/billing/subscription`. Each side maps its own
 * `Timestamp`s to epoch millis before calling in.
 */
export interface EntitlementInput {
  status: SubscriptionStatus
  tier: BillingTier
  /**
   * When the subscription entered `past_due`, in epoch millis. `null` when it isn't `past_due` —
   * or, defensively, when it is but the field is missing (see {@link resolveEntitlement}).
   */
  pastDueSinceMs: number | null
}

export interface Entitlement {
  tier: BillingTier
  active: boolean
}

/**
 * Resolve whether an account is entitled, and to what.
 *
 * | Status | Active | Why |
 * |---|---|---|
 * | `trialing` | yes | covers beta and trial alike |
 * | `active` | yes | |
 * | `past_due` | for {@link PAST_DUE_GRACE_DAYS} days | measured from `pastDueSinceMs` |
 * | `paused` | no | trial ended with no payment method |
 * | `canceled` | no | |
 * | `unpaid` | no | retries exhausted |
 * | `incomplete` | no | first payment never confirmed |
 * | `incomplete_expired` | no | |
 *
 * **Never throws.** A missing subscription is a normal state — an account that hasn't finished
 * provisioning, or one that predates billing entirely — so `null` resolves to not-entitled on the
 * lower tier rather than being an error. Tier is meaningless when inactive; defaulting to the
 * smaller one means an unknown answer fails safe in both directions.
 */
export function resolveEntitlement(sub: EntitlementInput | null, nowMs: number): Entitlement {
  if (!sub) {
    return { tier: BillingTier.Light, active: false }
  }

  const { tier, status } = sub

  switch (status) {
    case SubscriptionStatus.Trialing:
    case SubscriptionStatus.Active:
      return { tier, active: true }

    case SubscriptionStatus.PastDue: {
      // A `past_due` subscription with no `pastDueSinceMs` means the webhook's carve-out didn't
      // stamp it. Treating that as "grace hasn't started" would grant access indefinitely — the
      // exact bug the carve-out exists to prevent — so treat it as starting now: the account keeps
      // access for one grace window and then stops, rather than forever.
      const since = sub.pastDueSinceMs ?? nowMs
      return { tier, active: graceRemainingMs(since, nowMs) > 0 }
    }

    case SubscriptionStatus.Paused:
    case SubscriptionStatus.Canceled:
    case SubscriptionStatus.Unpaid:
    case SubscriptionStatus.Incomplete:
    case SubscriptionStatus.IncompleteExpired:
      return { tier, active: false }

    default:
      // A status Stripe added since this was written. Fail closed: an unrecognised state is not a
      // reason to serve an expensive operation.
      return { tier, active: false }
  }
}
